import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { FacebookLogo, InstagramLogo, LinkedinLogo, YoutubeLogo } from '@phosphor-icons/react';
import {
  PUBLIC_PHONE_DISPLAY,
  PUBLIC_PHONE_TEL_HREF,
  PUBLIC_INFO_EMAIL,
  PUBLIC_INFO_MAILTO_HREF,
} from '../constants/siteContact';
import { SITE_FOOTER_LOGO_SRC } from '../constants/branding';
import { TOP_LEVEL_CATEGORY_KEYS } from '../constants/catalogTaxonomy';
import { CATEGORIES, getCategoryTitle } from './CategoryGrid';

const SOCIAL_ITEMS = [
  { Icon: FacebookLogo, label: 'Facebook', href: null },
  { Icon: YoutubeLogo, label: 'YouTube', href: 'https://www.youtube.com/@Demaxtore' },
  { Icon: LinkedinLogo, label: 'LinkedIn', href: 'https://www.linkedin.com/company/demaxtore/' },
  { Icon: InstagramLogo, label: 'Instagram', href: null },
];

export default function Footer({ onNavigate, onCategorySelect }) {
  const { t, tl, lang } = useLanguage();

  const footerCategoryLabel = (categoryKey) => {
    const cat = CATEGORIES.find((c) => c.key === categoryKey);
    if (cat) return getCategoryTitle(cat, lang);
    if (categoryKey === 'Uncategorized') return t('footerCategoryUncategorized');
    return categoryKey;
  };

  return (
    <footer className="relative overflow-hidden bg-[#edf2fa]" data-testid="main-footer">
      <div className="px-[max(1.25rem,env(safe-area-inset-left))] pb-[max(3rem,env(safe-area-inset-bottom))] pr-[max(1.25rem,env(safe-area-inset-right))] pt-16 md:px-10 md:pt-20 lg:px-16">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
            <div className="lg:col-span-4">
              <button
                type="button"
                onClick={() => onNavigate && onNavigate('home')}
                className="mb-5 flex w-fit items-center py-1 text-left transition-opacity duration-200 hover:opacity-90"
                data-testid="footer-logo"
              >
                <img
                  src={SITE_FOOTER_LOGO_SRC}
                  alt={t('footerLogoAlt')}
                  className="h-auto w-auto max-h-[68px] max-w-[min(100%,min(92vw,640px))] object-contain object-left sm:max-h-[76px] lg:max-h-[84px]"
                  decoding="async"
                />
              </button>
              <p className="text-sm text-gray-600 leading-relaxed mb-6 max-w-xs font-sans">{t('footerDesc')}</p>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-x-5">
                {SOCIAL_ITEMS.map(({ Icon, label, href }) =>
                  href ? (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex text-[#4a4a4a] transition-opacity hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4a4a4a]/40 focus-visible:ring-offset-2 rounded-sm"
                      aria-label={label}
                      data-testid={`footer-social-${label.toLowerCase()}`}
                    >
                      <Icon size={26} weight="regular" className="shrink-0" aria-hidden />
                    </a>
                  ) : (
                    <span
                      key={label}
                      className="inline-flex cursor-default select-none text-[#4a4a4a]"
                      aria-label={label}
                      data-testid={`footer-social-${label.toLowerCase()}`}
                    >
                      <Icon size={26} weight="regular" className="shrink-0" aria-hidden />
                    </span>
                  ),
                )}
              </div>
            </div>

            <div className="lg:col-span-2">
              <h4 className="text-xs font-semibold text-[#dc2626] mb-5 tracking-wider uppercase font-sans">{t('quickLinks')}</h4>
              <ul className="space-y-3">
                {['home', 'products', 'about', 'blog', 'contact'].map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => onNavigate(key)}
                      className="text-sm text-gray-600 hover:text-[#0c2340] transition-colors font-sans"
                      data-testid={`footer-link-${key}`}
                    >
                      {t(key)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              <h4 className="text-xs font-semibold text-[#dc2626] mb-5 tracking-wider uppercase font-sans">{t('productCategories')}</h4>
              <ul className="space-y-3">
                {TOP_LEVEL_CATEGORY_KEYS.map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() =>
                        onCategorySelect
                          ? onCategorySelect({ category: key, subcategory: '', search: '' })
                          : onNavigate && onNavigate('products')
                      }
                      className="text-sm text-gray-600 hover:text-[#0c2340] transition-colors font-sans"
                    >
                      {footerCategoryLabel(key)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-3">
              <h4 className="text-xs font-semibold text-[#dc2626] mb-5 tracking-wider uppercase font-sans">{t('contactUs')}</h4>
              <div className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-sans uppercase tracking-wider">
                      {t('contactHeadOfficeLabel')}
                    </div>
                    <p className="text-sm text-gray-700 font-sans leading-relaxed">{t('contactHeadquartersAddressLine')}</p>
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 mb-1 font-sans uppercase tracking-wider">
                      {t('contactOfficeAddressLabel')}
                    </div>
                    <p className="text-sm text-gray-700 font-sans leading-relaxed">{t('contactBranchAddressLine')}</p>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 mb-1 font-sans uppercase tracking-wider">
                    {t('contactPublicEmailLabel')}
                  </div>
                  <p className="text-sm font-sans">
                    <a
                      href={PUBLIC_INFO_MAILTO_HREF}
                      className="text-[#0c2340] hover:text-[#dc2626] transition-colors"
                      data-testid="footer-email-link"
                    >
                      {PUBLIC_INFO_EMAIL}
                    </a>
                  </p>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 mb-1 font-sans uppercase tracking-wider">
                    {t('contactPublicPhoneLabel')}
                  </div>
                  <p className="text-sm text-gray-700 font-sans">
                    {PUBLIC_PHONE_TEL_HREF ? (
                      <a href={PUBLIC_PHONE_TEL_HREF} className="text-[#0c2340] hover:text-[#dc2626] transition-colors">
                        {PUBLIC_PHONE_DISPLAY}
                      </a>
                    ) : (
                      PUBLIC_PHONE_DISPLAY ||
                      tl('On request', 'Sur demande', 'Bajo consulta', 'حسب الطلب')
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 md:px-10 lg:px-16 py-5 border-t border-gray-200/90 relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-gray-500 font-sans">
            &copy; {new Date().getFullYear()} DeMaxtore. {t('allRights')}
          </span>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center sm:justify-end">
            <button
              type="button"
              onClick={() => onNavigate('kvkk')}
              className="text-xs text-gray-500 hover:text-[#0c2340] transition-colors font-sans"
              data-testid="footer-link-website-privacy"
            >
              {t('footerLinkWebsitePrivacy')}
            </button>
            <button
              type="button"
              onClick={() => onNavigate('gizlilik_politikasi')}
              className="text-xs text-gray-500 hover:text-[#0c2340] transition-colors font-sans"
              data-testid="footer-link-gizlilik"
            >
              {t('footerLinkPrivacy')}
            </button>
            <button
              type="button"
              onClick={() => onNavigate('cerez_politikasi')}
              className="text-xs text-gray-500 hover:text-[#0c2340] transition-colors font-sans"
              data-testid="footer-link-cerez"
            >
              {t('footerLinkCookies')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
